import { useState, useCallback, useEffect, useRef } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { Plus, MessageSquare } from 'lucide-react'
import { MainLayout } from '@/components/layouts'
import { PostCard, LeaderboardSidebar, CreatePostModal, FilterTabs } from '@/components/community'
import type { PageProps } from '@/types'
import { EditableContent } from '@/components/EditableContent'
import { SearchBar, Pagination } from '@/components/ui'
import type { CommunityPost, LeaderboardUser, PostCategory, EditableContent as EditableContentType, Pagination as PaginationType } from '@/types'

interface Filters {
  specialty: string | null
  category: string | null
}

interface CommunityProps {
  posts: CommunityPost[]
  pagination: PaginationType
  leaderboard: LeaderboardUser[]
  categories: PostCategory[]
  current_user_rank: number
  filters: Filters
  current_specialty: string
  editable_banner: EditableContentType | null
  query: string | null
}

export default function Community({ posts, pagination, leaderboard, categories, current_user_rank, filters, current_specialty, editable_banner, query }: CommunityProps) {
  const { props } = usePage<PageProps>()
  const isPatient = props.community?.slug === 'patients'
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState(query || '')
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedSearch = useCallback((value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      const params: Record<string, string> = {}
      if (value) params.q = value
      if (filters.specialty) params.specialty = filters.specialty
      if (filters.category) params.category = filters.category

      router.get('/community', params, {
        preserveState: true,
        preserveScroll: true
      })
    }, 300)
  }, [filters.specialty, filters.category])

  const handlePageChange = useCallback((page: number) => {
    const params: Record<string, string | number> = { page }
    if (searchQuery) params.q = searchQuery
    if (filters.specialty) params.specialty = filters.specialty
    if (filters.category) params.category = filters.category

    router.get('/community', params, {
      preserveState: true,
      preserveScroll: false
    })
  }, [searchQuery, filters.specialty, filters.category])

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    debouncedSearch(value)
  }

  // Determine active filter state
  const activeFilter = filters.specialty === 'mine' ? 'specialty' : 'all'
  const activeCategory = filters.category ? categories.find(c => c.slug === filters.category)?.name || filters.category : null

  return (
    <MainLayout
      title="Communauté"
    >
      <Head title="Communauté" />

      {/* Banner éditable (si existe) */}
      {editable_banner && (
        <EditableContent
          content={editable_banner}
          wrapperClassName="mb-8 -mt-8 -mx-4"
        />
      )}

      {/* Header with Title */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold text-gray-900">
          Publications récentes
        </h2>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-tessan-primary text-white rounded-lg hover:bg-tessan-primary-hover transition-colors font-medium"
        >
          <Plus className="w-4 h-4" />
          Créer un post
        </button>
      </div>

      {/* Barre de recherche */}
      <SearchBar
        value={searchQuery}
        onChange={handleSearch}
        placeholder="Rechercher une publication..."
        className="mb-6"
      />

      {/* Filter Tabs with Categories */}
      <FilterTabs
        activeFilter={activeFilter}
        activeCategory={activeCategory}
        currentSpecialty={current_specialty}
        categories={categories}
        showSpecialtyFilter={!isPatient}
      />

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content - Posts Feed */}
        <div className="lg:col-span-2 space-y-4">
          {/* Posts List */}
          {posts.length > 0 ? (
            <>
              <div className="space-y-4">
                {posts.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
              <Pagination pagination={pagination} onPageChange={handlePageChange} />
            </>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery ? 'Aucun résultat' : 'Aucune publication'}
              </h3>
              <p className="text-gray-500 mb-4">
                {searchQuery
                  ? `Aucune publication trouvée pour "${searchQuery}"`
                  : 'Soyez le premier à publier dans la communauté !'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-tessan-primary text-white rounded-lg hover:bg-tessan-primary-hover transition-colors font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Créer un post
                </button>
              )}
            </div>
          )}
        </div>

        {/* Sidebar - Leaderboard aligned with posts */}
        <div className="space-y-6">
          <LeaderboardSidebar
            users={leaderboard}
            currentUserRank={current_user_rank}
          />
        </div>
      </div>

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        categories={categories}
      />
    </MainLayout>
  )
}
